import React, { useState, useEffect } from 'react';
import { Modal, TextField, Button, BlockStack, InlineStack, Select, Checkbox, Text, Icon, Box } from '@shopify/polaris';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { DeleteIcon, DragHandleIcon } from '@shopify/polaris-icons';
import { getRequest, postRequest, putRequest, deleteRequest } from '../../../shared/components/functions';

const STATUS_COLORS = [
  { label: 'Blue (Info)', value: 'info' },
  { label: 'Yellow (Attention)', value: 'attention' },
  { label: 'Orange (Warning)', value: 'warning' },
  { label: 'Green (Success)', value: 'success' },
  { label: 'Red (Critical)', value: 'critical' },
  { label: 'Purple (New)', value: 'new' },
  { label: 'Gray (Default)', value: 'default' }
];

export default function StatusManagementModal({ open, onClose, onStatusesChange }) {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newStatusName, setNewStatusName] = useState('');
  const [newStatusColor, setNewStatusColor] = useState('info');
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    if (open) {
      fetchStatuses();
    }
  }, [open]);

  const fetchStatuses = async () => {
    setLoading(true);
    try {
      const data = await getRequest('/api/interaction-statuses');
      setStatuses(data);
    } catch (error) {
      console.error('Error fetching statuses:', error);
    }
    setLoading(false);
  };

  const handleAddStatus = async () => {
    if (!newStatusName.trim()) return;

    try {
      const newStatus = await postRequest('/api/interaction-statuses', {
        name: newStatusName.trim(),
        color: newStatusColor,
        is_kanban_visible: true
      });
      setStatuses([...statuses, newStatus]);
      setNewStatusName('');
      setNewStatusColor('info');
      onStatusesChange?.();
    } catch (error) {
      console.error('Error creating status:', error);
    }
  };

  const handleUpdateStatus = async (id, field, value) => {
    setSavingId(id);
    try {
      const status = statuses.find((s) => s.id === id);
      await putRequest(`/api/interaction-statuses/${id}`, {
        ...status,
        [field]: value
      });
      setStatuses(statuses.map((s) => (s.id === id ? { ...s, [field]: value } : s)));
      onStatusesChange?.();
    } catch (error) {
      console.error('Error updating status:', error);
    }
    setSavingId(null);
  };

  const handleDeleteStatus = async (id) => {
    if (!window.confirm('Are you sure you want to delete this status? Interactions with this status will have their status cleared.')) {
      return;
    }

    try {
      await deleteRequest(`/api/interaction-statuses/${id}`);
      setStatuses(statuses.filter((s) => s.id !== id));
      onStatusesChange?.();
    } catch (error) {
      console.error('Error deleting status:', error);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const { source, destination } = result;
    if (source.index === destination.index) return;

    const newStatuses = Array.from(statuses);
    const [removed] = newStatuses.splice(source.index, 1);
    newStatuses.splice(destination.index, 0, removed);

    // Update sort_order for all statuses
    const updatedStatuses = newStatuses.map((status, index) => ({
      ...status,
      sort_order: index
    }));

    setStatuses(updatedStatuses);

    try {
      await putRequest('/api/interaction-statuses/reorder', {
        statuses: updatedStatuses.map((s) => ({ id: s.id, sort_order: s.sort_order }))
      });
      onStatusesChange?.();
    } catch (error) {
      console.error('Error reordering statuses:', error);
      // Revert on error
      fetchStatuses();
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Manage Interaction Statuses" large>
      <Modal.Section>
        {loading ? (
          <Text>Loading...</Text>
        ) : (
          <BlockStack gap="400">
            <Text as="p" tone="subdued">
              Drag to reorder statuses. Toggle "Show in Kanban" to include a status as a column in the Kanban board view.
            </Text>

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="statuses">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    <BlockStack gap="200">
                      {statuses.map((status, index) => (
                        <Draggable key={status.id} draggableId={String(status.id)} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={{
                                ...provided.draggableProps.style,
                                background: snapshot.isDragging ? 'var(--p-color-bg-surface-hover)' : 'var(--p-color-bg-surface)',
                                borderRadius: '8px',
                                border: '1px solid var(--p-color-border)',
                                padding: '12px'
                              }}
                            >
                              <InlineStack gap="300" align="center" blockAlign="center">
                                <div {...provided.dragHandleProps} style={{ cursor: 'grab' }}>
                                  <Icon source={DragHandleIcon} tone="subdued" />
                                </div>

                                <div style={{ flex: 1 }}>
                                  <TextField
                                    value={status.name}
                                    onChange={(value) => {
                                      setStatuses(statuses.map((s) => (s.id === status.id ? { ...s, name: value } : s)));
                                    }}
                                    onBlur={() => handleUpdateStatus(status.id, 'name', status.name)}
                                    autoComplete="off"
                                    labelHidden
                                    label="Status name"
                                  />
                                </div>

                                <div style={{ width: '150px' }}>
                                  <Select options={STATUS_COLORS} value={status.color} onChange={(value) => handleUpdateStatus(status.id, 'color', value)} labelHidden label="Color" />
                                </div>

                                <Checkbox label="Show in Kanban" checked={status.is_kanban_visible} onChange={(value) => handleUpdateStatus(status.id, 'is_kanban_visible', value)} />

                                <Button icon={DeleteIcon} tone="critical" variant="plain" onClick={() => handleDeleteStatus(status.id)} accessibilityLabel="Delete status" />
                              </InlineStack>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </BlockStack>
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            <Box paddingBlockStart="400" borderBlockStartWidth="025" borderColor="border">
              <BlockStack gap="200">
                <Text as="h3" variant="headingSm">
                  Add New Status
                </Text>
                <InlineStack gap="300" align="end" blockAlign="end">
                  <div style={{ flex: 1 }}>
                    <TextField value={newStatusName} onChange={setNewStatusName} placeholder="Status name" autoComplete="off" label="Name" />
                  </div>
                  <div style={{ width: '150px' }}>
                    <Select options={STATUS_COLORS} value={newStatusColor} onChange={setNewStatusColor} label="Color" />
                  </div>
                  <Button onClick={handleAddStatus} disabled={!newStatusName.trim()} variant="primary">
                    Add Status
                  </Button>
                </InlineStack>
              </BlockStack>
            </Box>
          </BlockStack>
        )}
      </Modal.Section>
    </Modal>
  );
}

export { STATUS_COLORS };
